import React, { useEffect, useState } from "react";
import {
  Card,
  Select,
  Typography,
  Spin,
  Row,
  Col,
  Layout,
  message,
  Tabs,
  List,
} from "antd";
import {
  CloudOutlined,
  SunOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FireOutlined,
  ExperimentOutlined,
  FrownOutlined,
  ArrowUpOutlined, // Icon cho mặt trời mọc
  ArrowDownOutlined, // Icon cho mặt trời lặn
  SafetyCertificateOutlined, // Icon cho UV
} from "@ant-design/icons";
import axios from "axios";
import { PROVINCES, Province } from "./data";
import "./App.css";

const { Title, Text } = Typography;
const { Content } = Layout;
const { Option } = Select;

// --- Interfaces ---

interface WeatherData {
  temperature: number;
  windspeed: number;
  weathercode: number;
  humidity: number;
  time: string;
  // Các trường mới thêm
  sunrise: string;
  sunset: string;
  uvIndex: number;
}

interface DailyForecast {
  time: string;
  weathercode: number;
  tempMax: number;
  tempMin: number;
}

// --- Helpers ---
const getWeatherInfo = (code: number) => {
  const iconStyle = { fontSize: "4rem" };
  const listIconStyle = { fontSize: "1.5rem" };

  if (code === 0)
    return {
      text: "Trời quang",
      icon: <SunOutlined style={{ ...iconStyle, color: "#ff7e5f" }} />,
      listIcon: <SunOutlined style={{ ...listIconStyle, color: "#ff7e5f" }} />,
    };
  if (code >= 1 && code <= 3)
    return {
      text: "Có mây",
      icon: <CloudOutlined style={{ ...iconStyle, color: "#757f9a" }} />,
      listIcon: (
        <CloudOutlined style={{ ...listIconStyle, color: "#757f9a" }} />
      ),
    };
  if (code >= 45 && code <= 48)
    return {
      text: "Sương mù",
      icon: <CloudOutlined style={{ ...iconStyle, color: "#d7d2cc" }} />,
      listIcon: (
        <CloudOutlined style={{ ...listIconStyle, color: "#d7d2cc" }} />
      ),
    };
  if (code >= 51 && code <= 67)
    return {
      text: "Mưa rào",
      icon: <CloudOutlined style={{ ...iconStyle, color: "#4facfe" }} />,
      listIcon: (
        <CloudOutlined style={{ ...listIconStyle, color: "#4facfe" }} />
      ),
    };
  if (code >= 80 && code <= 99)
    return {
      text: "Mưa dông",
      icon: <ThunderboltOutlined style={{ ...iconStyle, color: "#f5222d" }} />,
      listIcon: (
        <ThunderboltOutlined style={{ ...listIconStyle, color: "#f5222d" }} />
      ),
    };
  return {
    text: "N/A",
    icon: <CloudOutlined style={{ ...iconStyle, color: "#999" }} />,
    listIcon: <CloudOutlined style={{ ...listIconStyle, color: "#999" }} />,
  };
};

const getDayName = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(dateString);

  if (checkDate.getTime() === today.getTime()) return "Hôm nay";

  return date.toLocaleDateString("vi-VN", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  });
};

// Hàm format giờ (VD: 2023-12-01T06:00 -> 06:00)
const formatTimeOnly = (isoString: string) => {
  return new Date(isoString).toLocaleTimeString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const App: React.FC = () => {
  const [selectedProvince, setSelectedProvince] = useState<Province>(
    PROVINCES[0]
  );
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [dailyForecast, setDailyForecast] = useState<DailyForecast[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchWeather = async (lat: number, lon: number) => {
    setLoading(true);
    try {
      // API Call MỚI: Thêm uv_index_max, sunrise, sunset vào daily
      const response = await axios.get(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&daily=weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset,uv_index_max&timezone=auto`
      );

      const current = response.data.current;
      const daily = response.data.daily;

      // Map dữ liệu hiện tại
      // Lưu ý: Sunrise/Sunset/UV nằm trong 'daily', ta lấy phần tử đầu tiên (index 0) cho ngày hiện tại
      setWeather({
        temperature: current.temperature_2m,
        windspeed: current.wind_speed_10m,
        weathercode: current.weather_code,
        humidity: current.relative_humidity_2m,
        time: current.time,
        sunrise: daily.sunrise[0],
        sunset: daily.sunset[0],
        uvIndex: daily.uv_index_max[0],
      });

      // Map dữ liệu dự báo 7 ngày
      const formattedDaily: DailyForecast[] = daily.time.map(
        (time: string, index: number) => ({
          time: time,
          weathercode: daily.weather_code[index],
          tempMax: daily.temperature_2m_max[index],
          tempMin: daily.temperature_2m_min[index],
        })
      );
      setDailyForecast(formattedDaily);
    } catch (error) {
      console.error(error);
      message.error("Không thể kết nối đến máy chủ thời tiết.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather(selectedProvince.lat, selectedProvince.lon);
  }, [selectedProvince]);

  const handleProvinceChange = (value: number) => {
    const province = PROVINCES.find((p) => p.id === value);
    if (province) setSelectedProvince(province);
  };

  const weatherInfo = weather ? getWeatherInfo(weather.weathercode) : null;

  // --- UI Components ---

  const CurrentWeatherView = () => (
    <div style={{ textAlign: "center", animation: "fadeIn 0.5s" }}>
      <div
        style={{ marginBottom: 10, animation: "float 3s ease-in-out infinite" }}
      >
        {weatherInfo?.icon}
      </div>

      <Title className="main-temp">{Math.round(weather!.temperature)}°</Title>

      <Title level={3} className="weather-desc">
        {weatherInfo?.text}
      </Title>

      {/* Grid thông tin chi tiết: 2 Hàng x 3 Cột = 6 Ô */}
      <Row gutter={[12, 12]} style={{ marginTop: 30 }}>
        {/* --- Hàng 1 --- */}
        <Col span={8}>
          <div
            className="info-box"
            style={{ padding: "10px 5px", height: "100%" }}
          >
            <DashboardOutlined
              style={{ fontSize: "20px", color: "#4facfe", marginBottom: 5 }}
            />
            <span className="info-label">Gió</span>
            <span className="info-value">
              {weather!.windspeed}{" "}
              <span style={{ fontSize: "0.7em" }}>km/h</span>
            </span>
          </div>
        </Col>

        <Col span={8}>
          <div
            className="info-box"
            style={{ padding: "10px 5px", height: "100%" }}
          >
            <ExperimentOutlined
              style={{ fontSize: "20px", color: "#1890ff", marginBottom: 5 }}
            />
            <span className="info-label">Độ ẩm</span>
            <span className="info-value">{weather!.humidity}%</span>
          </div>
        </Col>

        <Col span={8}>
          <div
            className="info-box"
            style={{ padding: "10px 5px", height: "100%" }}
          >
            <SafetyCertificateOutlined
              style={{ fontSize: "20px", color: "#faad14", marginBottom: 5 }}
            />
            <span className="info-label">UV Index</span>
            <span className="info-value">{weather!.uvIndex}</span>
          </div>
        </Col>

        {/* --- Hàng 2 --- */}
        <Col span={8}>
          <div
            className="info-box"
            style={{ padding: "10px 5px", height: "100%" }}
          >
            <ArrowUpOutlined
              style={{ fontSize: "20px", color: "#ff7e5f", marginBottom: 5 }}
            />
            <span className="info-label">Mọc</span>
            <span className="info-value">
              {formatTimeOnly(weather!.sunrise)}
            </span>
          </div>
        </Col>

        <Col span={8}>
          <div
            className="info-box"
            style={{ padding: "10px 5px", height: "100%" }}
          >
            <ArrowDownOutlined
              style={{ fontSize: "20px", color: "#757f9a", marginBottom: 5 }}
            />
            <span className="info-label">Lặn</span>
            <span className="info-value">
              {formatTimeOnly(weather!.sunset)}
            </span>
          </div>
        </Col>

        <Col span={8}>
          {/* Giữ lại ô Cập nhật để biết dữ liệu mới hay cũ */}
          <div
            className="info-box"
            style={{ padding: "10px 5px", height: "100%" }}
          >
            <ClockCircleOutlined
              style={{ fontSize: "20px", color: "#52c41a", marginBottom: 5 }}
            />
            <span className="info-label">Cập nhật</span>
            <span className="info-value">
              {new Date().toLocaleTimeString("vi-VN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        </Col>
      </Row>
    </div>
  );

  const DailyForecastView = () => (
    <div className="daily-list-container" style={{ animation: "fadeIn 0.5s" }}>
      <List
        dataSource={dailyForecast}
        renderItem={(item) => {
          const info = getWeatherInfo(item.weathercode);
          return (
            <div className="daily-item">
              <div className="daily-date">{getDayName(item.time)}</div>
              <div className="daily-icon">{info.listIcon}</div>
              <div className="daily-text">{info.text}</div>
              <div className="daily-temp">
                <span className="temp-max">{Math.round(item.tempMax)}°</span>
                <span className="temp-min">/ {Math.round(item.tempMin)}°</span>
              </div>
            </div>
          );
        }}
      />
    </div>
  );

  // --- Main Render ---
  return (
    <Layout className="app-layout">
      <Content
        style={{
          padding: "20px",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card className="weather-card" bordered={false}>
          {/* Header */}
          <div className="weather-header">
            <Title level={4} style={{ color: "#555", marginBottom: 5 }}>
              Dự Báo Thời Tiết
            </Title>
          </div>

          <Select
            showSearch
            className="custom-select"
            defaultValue={selectedProvince.id}
            style={{ width: "100%", marginBottom: 10 }}
            onChange={handleProvinceChange}
            placeholder="Tìm kiếm tỉnh thành..."
            filterOption={(input, option) => {
              if (!option) return false;
              const province = PROVINCES.find((p) => p.id === option.value);
              return province
                ? province.type.toLowerCase().includes(input.toLowerCase()) ||
                    province.name.toLowerCase().includes(input.toLowerCase())
                : false;
            }}
            suffixIcon={<EnvironmentOutlined style={{ color: "#ff7e5f" }} />}
            notFoundContent={
              <div style={{ textAlign: "center", padding: "12px" }}>
                <FrownOutlined
                  style={{ fontSize: 24, color: "#ff7e5f", marginBottom: 5 }}
                />
                <div style={{ color: "#777", fontSize: "0.9rem" }}>
                  Không tìm thấy kết quả
                </div>
              </div>
            }
          >
            {PROVINCES.map((province) => (
              <Option key={province.id} value={province.id}>
                {`${province.type} ${province.name}`}
              </Option>
            ))}
          </Select>

          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <Spin
                size="large"
                tip="Đang tải dữ liệu..."
                style={{ color: "#ff7e5f" }}
              />
            </div>
          ) : weather ? (
            <Tabs
              defaultActiveKey="1"
              centered
              className="weather-tabs"
              items={[
                {
                  key: "1",
                  label: (
                    <span>
                      <FireOutlined /> Hiện tại
                    </span>
                  ),
                  children: <CurrentWeatherView />,
                },
                {
                  key: "2",
                  label: (
                    <span>
                      <CalendarOutlined /> 7 Ngày tới
                    </span>
                  ),
                  children: <DailyForecastView />,
                },
              ]}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text type="secondary">Vui lòng chọn địa điểm</Text>
            </div>
          )}
        </Card>
      </Content>
    </Layout>
  );
};

export default App;
